import { z } from 'zod';

export const fieldTypeSchema = z.enum([
  'inputText',
  'inputNumber',
  'inputEmail',
  'textarea',
  'radio',
  'select',
  'checkbox',
  'numberSelect',
  'datePicker',
  'dateRangePicker',
  'timePicker',
  'scoreTable',
  'painScale',
]);
export type FieldType = z.infer<typeof fieldTypeSchema>;

export const OPERATOR = {
  '=': '=',
  '!=': '!=',
  '>': '>',
  '<': '<',
  '>=': '>=',
  '<=': '<=',
  contains: 'contains',
  notContains: 'notContains',
  isEmpty: 'isEmpty',
  notEmpty: 'notEmpty',
  incomplete: 'incomplete',
} as const;

export const FIELD_TYPE_OPERATORS: Record<FieldType, Operator[]> = {
  inputText: [
    OPERATOR['='],
    OPERATOR['!='],
    OPERATOR.contains,
    OPERATOR.notContains,
    OPERATOR.isEmpty,
    OPERATOR.notEmpty,
  ],
  inputNumber: [
    OPERATOR['='],
    OPERATOR['!='],
    OPERATOR['>'],
    OPERATOR['<'],
    OPERATOR['>='],
    OPERATOR['<='],
    OPERATOR.isEmpty,
    OPERATOR.notEmpty,
  ],
  inputEmail: [
    OPERATOR['='],
    OPERATOR['!='],
    OPERATOR.contains,
    OPERATOR.notContains,
    OPERATOR.isEmpty,
    OPERATOR.notEmpty,
  ],
  textarea: [
    OPERATOR.contains,
    OPERATOR.notContains,
    OPERATOR.isEmpty,
    OPERATOR.notEmpty,
  ],
  radio: [
    OPERATOR['='],
    OPERATOR['!='],
    OPERATOR.isEmpty,
    // FIXME: radio 應該不會有清空的機會，再跟 UI 確認一下
    OPERATOR.notEmpty,
  ],
  select: [OPERATOR['='], OPERATOR['!='], OPERATOR.isEmpty, OPERATOR.notEmpty],
  checkbox: [
    OPERATOR.contains,
    OPERATOR.notContains,
    OPERATOR.isEmpty,
    OPERATOR.notEmpty,
  ],
  numberSelect: [
    OPERATOR['='],
    OPERATOR['!='],
    OPERATOR['>'],
    OPERATOR['<'],
    OPERATOR['>='],
    OPERATOR['<='],
    OPERATOR.isEmpty,
    OPERATOR.notEmpty,
  ],
  datePicker: [
    OPERATOR['='],
    OPERATOR['!='],
    OPERATOR['>'],
    OPERATOR['<'],
    OPERATOR['>='],
    OPERATOR['<='],
    OPERATOR.isEmpty,
    OPERATOR.notEmpty,
  ],
  dateRangePicker: [
    OPERATOR['='],
    OPERATOR['!='],
    OPERATOR['>'],
    OPERATOR['<'],
    OPERATOR['>='],
    OPERATOR['<='],
    OPERATOR.isEmpty,
    OPERATOR.notEmpty,
    OPERATOR.incomplete,
  ],
  timePicker: [
    OPERATOR['='],
    OPERATOR['!='],
    OPERATOR['>'],
    OPERATOR['<'],
    OPERATOR['>='],
    OPERATOR['<='],
    OPERATOR.isEmpty,
    OPERATOR.notEmpty,
  ],
  scoreTable: [
    OPERATOR['='],
    OPERATOR['!='],
    OPERATOR['>'],
    OPERATOR['<'],
    OPERATOR['>='],
    OPERATOR['<='],
    OPERATOR.isEmpty,
    OPERATOR.notEmpty,
  ],
  painScale: [
    OPERATOR['='],
    OPERATOR['!='],
    OPERATOR['>'],
    OPERATOR['<'],
    OPERATOR['>='],
    OPERATOR['<='],
    OPERATOR.isEmpty,
    OPERATOR.notEmpty,
  ],
};

export const operatorSchema = z.enum(Object.values(OPERATOR));
export type Operator = z.infer<typeof operatorSchema>;

export const conjunctionSchema = z.enum(['AND', 'OR']);
export type Conjunction = z.infer<typeof conjunctionSchema>;

const singleConditionSchema = z
  .object({
    type: z.literal('single'),
    field: z.string().min(1),
    operator: operatorSchema,
    value: z.string(),
    conjunction: conjunctionSchema.optional(),
    isFieldDisabled: z.boolean().optional(),
  })
  .refine((data) => {
    if (
      data.operator === 'isEmpty' ||
      data.operator === 'notEmpty' ||
      data.operator === 'incomplete'
    ) {
      return data.value === '';
    }
    return data.value.length >= 1;
  });
export type SingleCondition = z.infer<typeof singleConditionSchema>;

const groupConditionSchema = z.object({
  type: z.literal('group'),
  conditions: z.array(singleConditionSchema),
});
export type GroupCondition = z.infer<typeof groupConditionSchema>;

export const templateConditionV1Schema = z.union([
  singleConditionSchema,
  groupConditionSchema,
]);

export type Condition = SingleCondition | GroupCondition;
